import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import InputField from 'src/components/common/InputField/InputField'
import MobiScrollCalender from 'src/components/common/MobiScrollCalendar/MobiScrollCalendar'
import { CFormLabel, CFormTextarea } from '@coreui/react-pro'
import moment from 'moment'
import {
  convertHyphenDateToSlashDate,
  convertSlashDateToHyphenDate,
} from '../../../../utils/sharedFunctions'
import FEATURES from '../../../../utils/featuresConstants'
import FacilityInfoCard from 'src/components/NewEncounter/FacilityCard/FacilityInfoCard'
import AdmitPatientEventCard from '../../census/components/censusTable/PatientEvent/AdmitPatientEventCard'
import DischargePatientEventCard from '../../census/components/censusTable/PatientEvent/DischargePatientEventCard'
import { saveEncounterField } from '../../../../redux/sidePanel/sidePanelThunk'
import { debouncer } from './sharedPanelComponents/sharedAIfunctions'
import LTCToggleBox from '../../../../components/common/ToggleBox/LTCToggleBox'
import { showLongTermCare } from '../../../../utils/sharedFeaturesFunctions'
import { setEncounterField } from '../../../../redux/sidePanel/sidePanelSlice'
import PanelCareTeams from './sharedPanelComponents/PanelCareTeams'
import {
  overwritePatientEvent,
  processPatientEventDate,
} from '../../census/components/censusTable/PatientEvent/sharedPatientEventFunctions'

export default function PanelFacilityCard() {
  const dispatch = useDispatch()
  const features = useSelector((state) => state?.user?.features) || []

  const panelEncounter = useSelector((state) => state.sidePanel.panelEncounter)
  const panelEncounterId = useSelector((state) => state.sidePanel.panelEncounter?._id)
  const isReadOnly = useSelector((state) => state.sidePanel.isReadOnly)
  const isLastEncounter = useSelector((state) => state.sidePanel.isLastEncounter)
  const dos = useSelector((state) => state.sidePanel.panelEncounter?.service_date_from)
  const facility_id = useSelector((state) => state.sidePanel.panelEncounter?.facility_id)
  const place_of_service = useSelector((state) => state.sidePanel.panelEncounter?.place_of_service)
  const admitDate = useSelector((state) => state.sidePanel.panelEncounter?.admission_date)
  const dischargeDate = useSelector((state) => state.sidePanel.panelEncounter?.discharge_date)
  const admitPatientEvent = useSelector(
    (state) => state.sidePanel.panelEncounter?.admitPatientEvent,
  )
  const dischargePatientEvent = useSelector(
    (state) => state.sidePanel.panelEncounter?.dischargePatientEvent,
  )
  const mrn = useSelector((state) => state.sidePanel.panelEncounter?.facility_mrn)
  const room = useSelector((state) => state.sidePanel.panelEncounter?.room)
  const admitReason = useSelector((state) => state.sidePanel.panelEncounter?.admission_reason)
  const ltc = useSelector((state) => state.sidePanel.panelEncounter?.long_term_care)
  const cptCodes = useSelector((state) => state.sidePanel.panelEncounter?.procedureCodes)

  const [stateMRN, setStateMRN] = useState(mrn || '')
  const [stateRoom, setStateRoom] = useState(room || '')
  const [stateAdmitReason, setStateAdmitReason] = useState(admitReason || '')

  const useInsuranceMapper = features?.includes(FEATURES.InsuranceMapper)

  const [patientAdmitEventDate, setPatientAdmitEventDate] = useState('')
  const [patientDischargeEventDate, setPatientDischargeEventDate] = useState('')

  const updateEncounter = async (field, value) => {
    await dispatch(
      setEncounterField({
        field,
        value,
      }),
    )
    debouncer(() => dispatch(saveEncounterField({})))
  }

  const processPatientEvent = async (patientEvent, date, type) => {
    patientEvent?.charge_id === panelEncounterId
      ? await overwritePatientEvent(date, patientEvent)
      : await processPatientEventDate({ ...panelEncounter, _id: panelEncounterId }, date, type)
  }

  // this logic is more convoluted because Classic needs to click 'Save' while Ai needs to insta save
  // so we need to track the new PE _id if we have it, and save it if we dont
  const processDischargePatientEvent = async (patientEvent, date, type) => {
    // if real PatientEvent exists
    if (patientEvent?.charge_id) {
      patientEvent?.charge_id === panelEncounterId
        ? await overwritePatientEvent(date, patientEvent)
        : await processPatientEventDate({ ...panelEncounter, _id: panelEncounterId }, date, type)
    }
    // no PE, we are working within panel only
    else {
      if (patientEvent?.date) {
        await overwritePatientEvent(date, {
          charge_id: panelEncounter._id,
          patient_id: panelEncounter.patient_id,
          service_date_from: panelEncounter?.service_date_from,
          type: type,
          _id: patientEvent._id,
        })
      } else {
        return await processPatientEventDate(
          { ...panelEncounter, _id: panelEncounterId },
          date,
          type,
        )
      }
    }
  }

  return (
    <div id={'PanelFacilityCard'} className={'display-box'}>
      <div className={'fac-details'} style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
        <FacilityInfoCard
          setSelectedFacility={(newFacName) => {
            updateEncounter('facility_name', newFacName)
          }}
          selectedFacilityId={facility_id}
          setSelectedFacilityId={(newFacId) => {
            updateEncounter('facility_id', newFacId)
          }}
          showPOS={features?.includes(FEATURES.PlaceOfServiceOverride)}
          pos={place_of_service}
          onNewPOSSelected={(newPOS) => {
            updateEncounter('place_of_service', newPOS)
          }}
          disabled={isReadOnly}
        >
          <div className="admit-ltc-wrapper">
            {useInsuranceMapper && (
              <div style={{ width: '50%', marginBottom: '0.2rem', pointerEvents: 'auto' }}>
                <AdmitPatientEventCard
                  visit={{ ...panelEncounter, _id: !isLastEncounter ? panelEncounter._id : '' }}
                  currentPatientAdmitDate={
                    patientAdmitEventDate
                      ? moment.utc(patientAdmitEventDate).format('MM/DD/YYYY')
                      : ''
                  }
                  onChange={async (newDate) => {
                    if (newDate === 'DISCHARGE') {
                      updateEncounter('admitPatientEvent', newDate)
                      setPatientAdmitEventDate('')
                      return
                    }
                    updateEncounter('admitPatientEvent', { date: newDate })
                    setPatientAdmitEventDate(newDate)
                    processPatientEvent(admitPatientEvent, newDate, 'ADMIT')
                  }}
                  current_service_date_from={convertHyphenDateToSlashDate(dos)}
                  current_cpts={cptCodes || []}
                  isAI={true}
                />
              </div>
            )}
            <div
              style={{
                width: useInsuranceMapper ? '50%' : '100%',
                paddingLeft: useInsuranceMapper ? '16px' : '0',
              }}
            >
              {showLongTermCare(features, place_of_service) && (
                <LTCToggleBox
                  onToggleChange={(value) => {
                    updateEncounter('long_term_care', value)
                  }}
                  isToggledOn={ltc}
                ></LTCToggleBox>
              )}
            </div>
          </div>
          <div className="admit-ltc-wrapper">
            {useInsuranceMapper && (
              <div style={{ width: '50%', marginBottom: '0.2rem', pointerEvents: 'auto' }}>
                <DischargePatientEventCard
                  visit={panelEncounter}
                  currentPatientDischargeDate={
                    patientDischargeEventDate
                      ? moment.utc(patientDischargeEventDate).format('MM/DD/YYYY')
                      : ''
                  }
                  onChange={async (newDate) => {
                    setPatientDischargeEventDate(newDate)
                    const patientEventId = await processDischargePatientEvent(
                      dischargePatientEvent,
                      newDate,
                      'DISCHARGE',
                    )
                    updateEncounter('dischargePatientEvent', {
                      ...dischargePatientEvent,
                      date: newDate,
                      source: 'API',
                      ...(patientEventId ? { _id: patientEventId } : {}),
                    })
                  }}
                  current_service_date_from={convertHyphenDateToSlashDate(dos)}
                />
              </div>
            )}
          </div>
        </FacilityInfoCard>
      </div>

      <div id={'FacilityAdmitInfo'} style={{ pointerEvents: isReadOnly ? 'none' : '' }}>
        {!useInsuranceMapper && (
          <>
            <div className={'admit-row'}>
              <CFormLabel className="label">Admission Date</CFormLabel>
              <MobiScrollCalender
                placeholder="MM / DD / YYYY"
                value={convertHyphenDateToSlashDate(admitDate)}
                type={'DOB'}
                handleDateChange={(date) => {
                  updateEncounter('admission_date', convertSlashDateToHyphenDate(date))
                }}
              ></MobiScrollCalender>
            </div>

            <div className={'discharge-row'}>
              <CFormLabel className="label">Discharge Date</CFormLabel>
              <MobiScrollCalender
                // label={invalidDOB ? 'Invalid Date Of Birth' : ''}
                placeholder="MM / DD / YYYY"
                value={convertHyphenDateToSlashDate(dischargeDate)}
                type={'DOB'}
                handleDateChange={(date) => {
                  updateEncounter('discharge_date', convertSlashDateToHyphenDate(date))
                }}
              ></MobiScrollCalender>
            </div>
          </>
        )}

        <div className={'mrn-row'}>
          <InputField
            label="Location"
            id="room"
            value={stateRoom}
            onChange={(e) => {
              const newRoom = e.currentTarget.value
              debouncer(() => updateEncounter('room', newRoom))
              setStateRoom(newRoom)
            }}
          />
        </div>

        <div className={'admit-reason-row'}>
          <CFormLabel className={'input-label'}>Admit Reason/Chief Complaint</CFormLabel>
          <CFormTextarea
            className={'default-text-area '}
            rows={3}
            value={stateAdmitReason}
            onChange={(e) => {
              const newReason = e.currentTarget.value
              debouncer(() => updateEncounter('admission_reason', newReason))
              setStateAdmitReason(newReason)
            }}
          />
        </div>
        <PanelCareTeams
          onChange={(newCareTeam) => {
            updateEncounter('careteam', newCareTeam)
          }}
        />
        <div className={'mrn-row'}>
          <InputField
            label="Facility MRN"
            id="mrn"
            value={stateMRN}
            onChange={(e) => {
              const newMRN = e.currentTarget.value
              debouncer(() => updateEncounter('facility_mrn', newMRN))
              setStateMRN(newMRN)
            }}
          />
        </div>
      </div>
    </div>
  )
}
